import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { LabelEditor, createEmptyDocument, useEditorStore } from '@/features/label-editor';
import {
  createModeloEtiqueta,
  labelTemplateKeys,
} from '@/services/labelTemplateService';
import type { ILabelDocument } from '@/features/label-editor/domain/types';
import './modelos-etiqueta.css';

export const Route = createFileRoute('/_authenticated/configuracao/modelos-etiqueta/form/')({
  component: NovoModeloEtiquetaPage,
});

const REDIRECT_DELAY_MS = 900;

function NovoModeloEtiquetaPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const markClean = useEditorStore((s) => s.markClean);
  const initial = useMemo(() => createEmptyDocument('Nova etiqueta', 100, 50, 203), []);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (doc: ILabelDocument) =>
      createModeloEtiqueta({
        nome: doc.name,
        largura_mm: doc.width_mm,
        altura_mm: doc.height_mm,
        dpi: doc.dpi,
        documento: doc,
        ativo: true,
      }),
    onSuccess: async (created) => {
      markClean();
      await queryClient.invalidateQueries({ queryKey: labelTemplateKeys.all });
      setCreatedId(created.id);
    },
  });

  useEffect(() => {
    if (!createdId) return;
    const timeout = setTimeout(() => {
      void navigate({
        to: '/configuracao/modelos-etiqueta/form/$id',
        params: { id: createdId },
      });
    }, REDIRECT_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [createdId, navigate]);

  return (
    <div className="modelos-etiqueta-page modelos-etiqueta-page--editor">
      <div className="modelos-etiqueta-page__header">
        <h2>Novo modelo de etiqueta</h2>
        <Link
          to="/configuracao/modelos-etiqueta"
          className="modelos-etiqueta-page__back"
        >
          Voltar à lista
        </Link>
      </div>
      <LabelEditor
        initialDocument={initial}
        saving={mutation.isPending}
        onSave={(doc) => mutation.mutateAsync(doc)}
      />
      {mutation.isSuccess && (
        <p className="modelos-etiqueta-page__ok">Modelo criado com sucesso.</p>
      )}
      {mutation.isError && (
        <p className="modelos-etiqueta-page__error">Falha ao salvar modelo.</p>
      )}
    </div>
  );
}
